import React, { useState } from 'react';
import { Eye, Edit, FileText, Share2, Trash2, Mail, Copy, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { generarPDFGestion } from '../utils/pdfGenerator';
import { useAuth } from '../context/AuthContext';
import { htmlToPlainText } from '../utils/htmlToPlainText';
import './AccionesGestion.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AccionesGestion = ({ 
  gestion, 
  onView, 
  onEdit, 
  onDelete,
  showDelete = false,
  compact = false,
  canEdit = true  // Prop para controlar si se puede editar
}) => {
  const { canEditCases, isViewer } = useAuth();
  
  // Determinar si el botón de editar debe estar habilitado
  const editDisabled = !canEdit || isViewer() || !canEditCases();
  const [showShareModal, setShowShareModal] = useState(false);
  const [copied, setCopied] = useState(false);

  // Generar texto para compartir
  const generarTextoCompartir = () => {
    const campos = gestion.campos_especificos || {};
    let camposTexto = '';
    
    if (Object.keys(campos).length > 0) {
      camposTexto = '\n📋 *Campos Específicos:*\n';
      Object.entries(campos).forEach(([key, value]) => {
        if (value && !Array.isArray(value) && typeof value !== 'object') {
          const label = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
          camposTexto += `  • ${label}: ${value}\n`;
        }
      });
    }

    // Texto de introducción estándar
    const textoIntro = `Por medio del presente, se le informa que se le ha asignado una nueva gestión para su conocimiento, análisis y atención correspondiente, conforme a los procedimientos establecidos. La información detallada se encuentra disponible en el sistema, para las acciones que estime pertinentes dentro del ámbito de sus funciones.`;

    // Texto de cierre estándar
    const textoCierre = `Sin otro particular, quedamos atentos a cualquier comentario o requerimiento adicional que considere necesario. Agradecemos su atención y el seguimiento oportuno a la presente gestión.`;

    // Incluir análisis complementario si existe
    let analisisTexto = '';
    if (gestion.analisis_complementario) {
      analisisTexto = `\n📊 *Análisis Complementario (Asesor CNC):*\n${htmlToPlainText(gestion.analisis_complementario)}\n`;
    }

    // Convertir descripción HTML a texto plano
    const descripcionTexto = htmlToPlainText(gestion.descripcion) || 'Sin descripción';

    return `📌 *NOTIFICACIÓN DE GESTIÓN*

${textoIntro}

━━━━━━━━━━━━━━━━━━━━
📋 *${gestion.nomenclatura}* - ${gestion.tipo_gestion}
━━━━━━━━━━━━━━━━━━━━

🏢 *Institución:* ${gestion.institucion}
👤 *Asesor CNC:* ${gestion.nombre_asesor_cnc}
${gestion.nombre_asesor_instancia ? `👥 *Asesor Instancia:* ${gestion.nombre_asesor_instancia}\n` : ''}📅 *Fecha Asignación:* ${new Date(gestion.fecha_asignacion).toLocaleDateString('es-ES')}
${gestion.fecha_vencimiento ? `⏰ *Fecha Vencimiento:* ${new Date(gestion.fecha_vencimiento).toLocaleDateString('es-ES')}\n` : ''}📊 *Estado:* ${gestion.estado}

📝 *Descripción:*
${descripcionTexto}
${analisisTexto}${camposTexto}
━━━━━━━━━━━━━━━━━━━━

${textoCierre}

_Sistema ALERTA 360 - CNC Guatemala_`;
  };

  // Manejar generación de PDF
  const handleGenerarPDF = async () => {
    try {
      await generarPDFGestion(gestion);
      toast.success('PDF generado correctamente');
    } catch (error) {
      console.error('Error al generar PDF:', error);
      toast.error('Error al generar PDF');
    }
  };

  // Manejar compartir nativo
  const handleCompartirNativo = async () => {
    const texto = generarTextoCompartir();
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${gestion.nomenclatura} - ${gestion.tipo_gestion}`,
          text: texto,
          url: window.location.href
        });
        toast.success('Compartido correctamente');
        setShowShareModal(false);
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Error al compartir:', error);
          toast.error('Error al compartir');
        }
      }
    } else {
      // Fallback: copiar al portapapeles
      handleCopiarTexto();
    }
  };

  // Copiar texto al portapapeles
  const handleCopiarTexto = async () => {
    const texto = generarTextoCompartir();
    try {
      await navigator.clipboard.writeText(texto);
      setCopied(true);
      toast.success('Texto copiado al portapapeles');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Error al copiar:', error);
      toast.error('Error al copiar');
    }
  };

  // Compartir por correo
  const handleCompartirEmail = () => {
    const texto = generarTextoCompartir().replace(/\*/g, ''); // Remover formato markdown
    const subject = encodeURIComponent(`${gestion.nomenclatura} - ${gestion.tipo_gestion}`);
    const body = encodeURIComponent(texto);
    window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
    toast.success('Abriendo cliente de correo...');
    setShowShareModal(false);
  };

  // Compartir por WhatsApp
  const handleCompartirWhatsApp = () => {
    const texto = encodeURIComponent(generarTextoCompartir());
    window.open(`https://wa.me/?text=${texto}`, '_blank');
    toast.success('Abriendo WhatsApp...');
    setShowShareModal(false);
  };

  return (
    <>
      <div className={`acciones-gestion ${compact ? 'compact' : ''}`}>
        <button
          className="btn-accion btn-ver"
          onClick={() => onView && onView(gestion)}
          title="Ver detalle"
        >
          <Eye size={compact ? 14 : 16} />
          {!compact && <span>Ver</span>}
        </button>

        <button
          className="btn-accion btn-editar"
          onClick={() => onEdit && onEdit(gestion)}
          title={editDisabled ? "No tiene permisos para editar" : "Editar"}
          disabled={editDisabled}
          style={editDisabled ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
        >
          <Edit size={compact ? 14 : 16} />
          {!compact && <span>Editar</span>}
        </button>

        <button
          className="btn-accion btn-pdf"
          onClick={handleGenerarPDF}
          title="Generar PDF"
        >
          <FileText size={compact ? 14 : 16} />
          {!compact && <span>PDF</span>}
        </button>

        <button
          className="btn-accion btn-compartir"
          onClick={() => setShowShareModal(true)}
          title="Compartir"
        >
          <Share2 size={compact ? 14 : 16} />
          {!compact && <span>Compartir</span>}
        </button>

        {showDelete && (
          <button
            className="btn-accion btn-eliminar"
            onClick={() => onDelete && onDelete(gestion)}
            title="Eliminar"
          >
            <Trash2 size={compact ? 14 : 16} />
            {!compact && <span>Eliminar</span>}
          </button>
        )}
      </div>

      {/* Modal de Compartir */}
      {showShareModal && (
        <div className="modal-overlay" onClick={() => setShowShareModal(false)}>
          <div className="modal-compartir" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                <Share2 size={20} />
                Compartir Gestión
              </h3>
              <button className="btn-cerrar" onClick={() => setShowShareModal(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="modal-body">
              <div className="gestion-preview">
                <div className="preview-header" style={{ borderLeftColor: getModuloColor(gestion.tipo_gestion) }}>
                  <strong>{gestion.nomenclatura}</strong>
                  <span className="tipo-badge">{gestion.tipo_gestion}</span>
                </div>
                <p className="preview-desc">
                  {(() => {
                    const plainDesc = htmlToPlainText(gestion.descripcion);
                    return plainDesc ? `${plainDesc.slice(0, 150)}${plainDesc.length > 150 ? '...' : ''}` : 'Sin descripción';
                  })()}
                </p>
              </div>

              <div className="opciones-compartir">
                <button className="btn-opcion btn-nativo" onClick={handleCompartirNativo}>
                  <Share2 size={20} />
                  <div>
                    <strong>Compartir Nativo</strong>
                    <span>Usar opciones del sistema</span>
                  </div>
                </button>

                <button className="btn-opcion btn-whatsapp" onClick={handleCompartirWhatsApp}>
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  <div>
                    <strong>WhatsApp</strong>
                    <span>Compartir por WhatsApp</span>
                  </div>
                </button>

                <button className="btn-opcion btn-email" onClick={handleCompartirEmail}>
                  <Mail size={20} />
                  <div>
                    <strong>Correo Electrónico</strong>
                    <span>Enviar por email</span>
                  </div>
                </button>

                <button className="btn-opcion btn-copiar" onClick={handleCopiarTexto}>
                  {copied ? <Check size={20} /> : <Copy size={20} />}
                  <div>
                    <strong>{copied ? '¡Copiado!' : 'Copiar Texto'}</strong>
                    <span>Copiar al portapapeles</span>
                  </div>
                </button>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowShareModal(false)}>
                Cerrar
              </button>
              <button className="btn btn-primary" onClick={handleGenerarPDF}>
                <FileText size={16} />
                Descargar PDF
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Función auxiliar para obtener color del módulo
const getModuloColor = (tipo) => {
  const colores = {
    // Nuevos nombres
    'Monitoreo': '#2196F3',
    'Adquisiciones públicas': '#F44336',
    'Contratación Personal': '#4CAF50',
    'Dirección Ejecutiva': '#FF9800',
    'Casos previos': '#9C27B0',
    // Nombres legacy
    'Monitoreo': '#2196F3',
    'Adquisiciones públicas': '#F44336',
    'Contratación Personal': '#4CAF50',
    'Dirección Ejecutiva': '#FF9800',
    'Casos previos': '#9C27B0',
    // Compatibilidad con nombres antiguos
    'Monitoreo de Medios': '#2196F3',
    'Banderas Rojas': '#F44336',
    'Requerimientos de Instancias de Probidad': '#4CAF50',
    'Contratación Personal': '#4CAF50',
    'Gestión de Información': '#FF9800',
    'Seguimiento': '#9C27B0'
  };
  return colores[tipo] || '#757575';
};

export default AccionesGestion;
